"use client";

import { useEffect, useState, useRef } from "react";

const fundingStages = [
  { id: "start", label: "", amount: 0 },
  { id: "pre-seed", label: "Pre-Seed", amount: 1 },
  { id: "seed", label: "Seed", amount: 8 },
  { id: "series-a", label: "Series A", amount: 31 },
];

export function FundingTimeline() {
  const [progress, setProgress] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection Observer to trigger animation when scrolled into view
  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    // Check if already in view on mount
    const rect = element.getBoundingClientRect();
    // If the top of the element is within the viewport (below top edge, above bottom edge)
    // OR if the bottom of the element is within the viewport
    // "1/3 up the page" = trigger when element is in the top 2/3 of viewport?
    // User said: "scrolled into view 1/3 up the page". Usually means element bottom crosses line 1/3 from bottom of screen.
    // Which is rect.top < window.innerHeight * 0.67.
    const isInView = rect.top < window.innerHeight * 0.67;

    if (isInView) {
      setShouldAnimate(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldAnimate(true);
            observer.disconnect();
          }
        });
      },
      {
        // rootMargin bottom -33% means viewport is effectively 67% height from top.
        // So intersection happens when element enters top 67% of screen.
        rootMargin: "0px 0px -33% 0px",
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Animation effect - only runs when shouldAnimate is true
  useEffect(() => {
    if (!shouldAnimate) return;

    let frame: number;
    let start: number | null = null;
    const duration = 1125;
    const easeOutCubic = (value: number) =>
      1 - Math.pow(1 - Math.min(Math.max(value, 0), 1), 3);

    const animate = (timestamp: number) => {
      if (start === null) {
        start = timestamp;
      }
      const elapsed = timestamp - start;
      const linearProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(linearProgress);
      setProgress(easedProgress);

      if (linearProgress < 1) {
        frame = requestAnimationFrame(animate);
      }
    };

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [shouldAnimate]);

  const segmentCount = fundingStages.length - 1;
  const normalized =
    segmentCount <= 0 ? 0 : Math.min(segmentCount, progress * segmentCount);
  const lowerIndex = Math.floor(normalized);
  const upperIndex = Math.min(fundingStages.length - 1, lowerIndex + 1);
  const fraction = normalized - lowerIndex;

  const lowerStage = fundingStages[lowerIndex] ?? fundingStages[0];
  const upperStage =
    fundingStages[upperIndex] ?? fundingStages[fundingStages.length - 1];

  const animatedAmount =
    (lowerStage?.amount ?? 0) +
    ((upperStage?.amount ?? 0) - (lowerStage?.amount ?? 0)) * fraction;

  const displayStageIndex = Math.min(
    fundingStages.length - 1,
    Math.round(normalized)
  );

  const displayStage = fundingStages[displayStageIndex] ?? fundingStages[0];

  const formatMillions = (value: number) => {
    const formatted =
      value >= 10 ? Math.round(value).toString() : value.toFixed(1);
    return `${formatted}M`;
  };

  const progressPercent = `${Math.min(progress * 50, 50).toFixed(2)}%`;

  return (
    <section ref={sectionRef} className="w-full">
      <div className="relative h-2 w-full overflow-visible rounded-full">
        <div className="absolute inset-0 rounded-full bg-black/10" />
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8f91ff] to-[#5d60d0]"
          style={{ width: progressPercent }}
        />
        <span
          className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white/60 bg-[#8f91ff] shadow-[0_0_15px_rgba(143,145,255,0.4)]"
          style={{ left: progressPercent }}
        />
      </div>
      <div className="relative mt-6 h-16">
        <div
          className="absolute flex flex-col items-center text-center transition-transform duration-75"
          style={{
            left: progressPercent,
            transform: `translateX(${progress < 0.15 ? "0%" : "-50%"})`,
          }}
        >
          <p className="font-ibm-plex-mono text-[11px] uppercase tracking-[0.4em] text-[#0a0a0a]/60">
            {displayStage?.label}
          </p>
          <p className="font-arizona-mix text-3xl text-[#7678e3] sm:text-[40px]">
            ${formatMillions(animatedAmount)}
          </p>
        </div>
      </div>
    </section>
  );
}
